<template>
  <DataTable
    ref="dataTable"
    tableStyle="min-width: 50rem"
    table-class="table-colleges"
    :value="shownColleges"
    :rows="20"
    :rowsPerPageOptions="[10, 20, 50, 100]"
    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
    currentPageReportTemplate="Showing {first}–{last} of {totalRecords}"
    sortMode="multiple"
    :multiSortMeta="defaultSort"
    paginator
    scrollable
    scrollHeight="flex"
    @page="onPage"
    @sort="onPage"
  >
    <Column field="institutionName" sortable header="Institution" style="width: 25ch">
      <template #body="slotProps">
        <span class="text-sm">{{ slotProps.data.institutionName }}</span>
      </template>
    </Column>
    <Column field="stateName" sortable header="State" style="width:12ch;">
      <template #body="slotProps">
        <span class="text-sm">{{ slotProps.data.stateName }}</span>
      </template>
    </Column>
    <Column field="level" sortable header="Level" style="width:12ch;">
      <template #body="slotProps">
        <span class="text-sm">{{ levels.find((el) => el.code == slotProps.data.level)?.name }}</span>
      </template>
    </Column>
    <Column field="control" style="width: 13ch;" sortable header="Control">
      <template #body="slotProps">
        <span class="text-sm">{{ control?.find((el) => el.code == slotProps.data.control)?.name }}</span>
      </template>
    </Column>
    <Column field="academicAssociation" sortable header="Classification" style="width:16ch;">
      <template #body="slotProps">
        <span class="text-sm">{{ classificationLabel(slotProps.data.academicAssociation) }}</span>
      </template>
    </Column>

    <!-- Catalog column: coverage only. The dropdown lists which editions
         DegreeData holds and stops there — every option is disabled, so it
         reads as a list rather than a picker. Reading a catalog happens in
         CurriculumFinder, so the header sells the trial that unlocks it. -->
    <Column style="width: 22ch;">
      <template #header>
        <div class="catalog-head">
          <span>Catalog</span>
          <a
            :href="trialHref"
            target="_blank"
            rel="noopener noreferrer"
            class="catalog-head__cta"
            @click="trackTrialClick"
          >
            Start a free trial
            <span class="catalog-head__cta-arrow" aria-hidden="true">→</span>
          </a>
        </div>
      </template>
      <template #body="slotProps">
        <Dropdown
          :options="catalogs[slotProps.data.id] || []"
          optionLabel="edition"
          :optionDisabled="() => true"
          placeholder="View available editions"
          class="w-full"
          @before-show="onClickCatalogs(slotProps.data.id)"
        >
          <template #empty>
            <div class="py-3 px-5 text-sm">
              {{ catalogs[slotProps.data.id] === undefined ? 'Loading catalogs...' : 'No catalogs found' }}
            </div>
          </template>
        </Dropdown>
      </template>
    </Column>

    <template #empty>
      <p class="text-left text-sm font-bold py-6">
        <template v-if="isLoading">
          <svg class="animate-spin inline -ml-1 mr-1 h-4 w-4 text-mute" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading colleges...
        </template>
        <template v-else-if="loadFailed">
          Couldn't load institutions. Please refresh the page to try again.
        </template>
        <template v-else>
          No results found
        </template>
      </p>
    </template>
  </DataTable>
</template>

<script>
import { states, levels, control, classificationLabel } from '@/data/filterOptions';

const catalogCacheKey = (institutionId) => `college-search--catalog-cache--${institutionId}`;

export default {
  name: 'CollegeTable',

  props: {
    // Flat filter model owned by App.vue: { state, level, control }.
    // Every institution loads on mount; all filters narrow rows in `shownColleges`.
    filters: {
      type: Object,
      required: true,
    },
  },

  beforeMount() {
    // Redirect users hitting the retired AcademicSearch hostname onto the
    // canonical CoverageReport domain. All other hosts (localhost, staging,
    // production, preview URLs) render normally.
    if (window.location.hostname === 'academicsearch.degreedata.com') {
      window.location.href =
        'https://coveragereport.degreedata.com' +
        window.location.pathname +
        window.location.search;
      return;
    }

    this.fetchColleges();
  },

  data() {
    return {
      levels,
      control,
      urls: {
        catalogViewer: process.env.VUE_APP_CATALOG_VIEWER_URL,
      },
      catalogs: {},
      colleges: [],
      isLoading: false,
      loadFailed: false,
      // Initial order: by state, then by institution within each state.
      defaultSort: [
        { field: 'stateName', order: 1 },
        { field: 'institutionName', order: 1 },
      ],
    };
  },

  computed: {
    // CurriculumFinder greets a signed-out visitor with its auth panel, which
    // opens on the Sign Up tab — a no-card 30-day trial.
    trialHref() {
      return this.urls.catalogViewer;
    },

    shownColleges() {
      // Every active filter must match (logical AND).
      return this.colleges.filter((college) => {
        if (this.filters.state && college.state !== this.filters.state) {
          return false;
        }

        if (this.filters.level && college.level !== this.filters.level) {
          return false;
        }

        if (this.filters.control && college.control !== this.filters.control) {
          return false;
        }

        return true;
      });
    },
  },

  watch: {
    // Filtering changes how many rows the scroll body holds.
    shownColleges() {
      this.$nextTick(this.updateScrollFade);
    },

    // Analytics payloads are deliberately FLAT. GA4 event parameters must be
    // scalars — a nested object is rendered as the string "[object Object]", so
    // the previous `filter: { type, value }` shape arrived in GA4 carrying no
    // usable data at all. Verified by reading live /g/collect payloads.
    'filters.state'(newState) {
      if (newState) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'filter_by_state',
          filter_type: 'state',
          filter_value: newState,
        });
      }
    },

    'filters.level'(newLevel) {
      if (newLevel) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'filter_by_level',
          filter_type: 'level',
          filter_value: newLevel,
        });
      }
    },

    'filters.control'(newControl) {
      if (newControl) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'filter_by_control',
          filter_type: 'control',
          filter_value: newControl,
        });
      }
    },

  },

  mounted() {
    this.scrollBody = this.$refs.dataTable?.$el.querySelector('[data-pc-section="wrapper"]');
    this.scrollBody?.addEventListener('scroll', this.updateScrollFade, { passive: true });
    window.addEventListener('resize', this.updateScrollFade);
  },

  beforeUnmount() {
    this.scrollBody?.removeEventListener('scroll', this.updateScrollFade);
    window.removeEventListener('resize', this.updateScrollFade);
  },

  methods: {
    classificationLabel,

    // A page of rows can run past the bottom of the scroll body, and with the
    // last visible row sitting flush on the paginator it reads as the end of
    // the list. Fade the bottom edge while rows remain below it.
    updateScrollFade() {
      const el = this.scrollBody;
      if (!el) return;
      const moreBelow = el.scrollHeight - el.scrollTop - el.clientHeight > 1;
      el.classList.toggle('has-more-below', moreBelow);
    },

    trackTrialClick() {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'start_free_trial',
        cta_location: 'catalog_column_header',
      });
    },

    // The full dataset (~3,000 rows) loads once on page load so visitors see
    // results immediately; every filter then works client-side.
    async fetchColleges() {
      this.isLoading = true;
      this.loadFailed = false;
      try {
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/institutions/all`, {
          method: 'GET',
          headers: {
            'x-api-key': process.env.VUE_APP_API_KEY,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        // Rows carry a state code ("IA"); sort on the displayed name so the
        // order matches what visitors read (Idaho before Iowa).
        const stateNames = Object.fromEntries(states.map((s) => [s.code, s.name]));
        this.colleges = (data.message || []).map((college) => ({
          ...college,
          stateName: stateNames[college.state] ?? college.state,
        }));
      } catch (error) {
        console.error('Error fetching institutions:', error);
        this.colleges = [];
        this.loadFailed = true;
      } finally {
        this.isLoading = false;
      }
    },

    async getCatalogs(institutionId) {
      if (this.catalogs[institutionId]) {
        return this.catalogs[institutionId];
      }

      try {
        const cached = JSON.parse(localStorage.getItem(catalogCacheKey(institutionId)) ?? '[]');
        if (Array.isArray(cached) && cached.length) {
          this.catalogs[institutionId] = cached;
          return cached;
        }
      } catch (error) {
        console.error('Error reading cached catalogs:', error);
      }

      try {
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/catalogs/institution?id=${institutionId}`, {
          method: 'GET',
          headers: {
            'x-api-key': process.env.VUE_APP_API_KEY,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });
        const json = await response.json();
        const data = (json.message || []).sort((a, b) => parseInt(b.edition, 10) - parseInt(a.edition, 10));
        localStorage.setItem(catalogCacheKey(institutionId), JSON.stringify(data));
        this.catalogs[institutionId] = data;
        return data;
      } catch (error) {
        console.error('Error fetching catalogs:', error);
        return [];
      }
    },

    async onClickCatalogs(institutionId) {
      const catalogs = await this.getCatalogs(institutionId);
      this.catalogs[institutionId] = catalogs;
    },

    // When the user pages, the scroll body keeps its previous offset, leaving
    // them mid-table on the new page. Reset it back to the first row.
    onPage() {
      this.$nextTick(() => {
        const root = this.$refs.dataTable?.$el;
        if (!root) return;

        // Desktop app-shell: the table body is its own scroll region.
        const scrollBody = root.querySelector('[data-pc-section="wrapper"]');
        scrollBody?.scrollTo({ top: 0, behavior: 'smooth' });

        // Stacked layout: the page scrolls instead — bring the table back up.
        root.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

        this.updateScrollFade();
      });
    },
  },
};
</script>

<style scoped>
/* The datatable preset sets the inner <table> to h-full. Once the scroll body
   has a bounded height, that stretches a short result set to fill it and
   spreads the rows apart. Pin the table to its natural height instead. */
:deep(table.table-colleges) {
  height: auto;
}

/* Bottom-edge fade while more rows sit below the fold (see updateScrollFade).
   A mask on the scroll container fades whatever is in view at its bottom edge,
   regardless of row backgrounds. */
:deep([data-pc-section="wrapper"].has-more-below) {
  -webkit-mask-image: linear-gradient(to bottom, #000 calc(100% - 56px), transparent);
  mask-image: linear-gradient(to bottom, #000 calc(100% - 56px), transparent);
}

/* The default sort is state-then-institution, so PrimeVue numbers both
   headers ("1", "2"). The arrows already show what's sorted; drop the badges. */
:deep([data-pc-section="sortbadge"]) {
  display: none;
}

/* Paginator row count ("Showing 1–20 of 2962"). */
:deep([data-pc-section="current"]) {
  margin-left: 12px;
  font-size: 13px;
  color: var(--mute);
}

/* Catalog header: title left, trial CTA pushed to the column's right edge so it
   lines up with the right edge of the edition dropdowns below it. */
.catalog-head {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  /* Header cells carry px-1, body cells p-2 — the extra 4px lands the pill on
     the same right edge as the dropdowns below. */
  padding-right: 4px;
  line-height: 1.2;
}

/* A scaled-down .ds-button--primary: enough weight to read as a call to action
   in a row of column titles, small enough not to shout over them. */
.catalog-head__cta {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 11px;
  border-radius: var(--r-pill);
  background: var(--ink);
  color: #fff;
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
  text-decoration: none;
  transition: background 0.15s ease;
}

.catalog-head__cta:hover {
  background: #000;
}

.catalog-head__cta-arrow {
  transition: transform 0.15s ease;
}

.catalog-head__cta:hover .catalog-head__cta-arrow {
  transform: translateX(2px);
}
</style>
